import { useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { FC, InfoSection, Link, SvgIcon } from ".."
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"

interface RulesAndRequirementsProps {
  onOpenTermsModal?: () => void
}

const RulesAndRequirements: FC<RulesAndRequirementsProps> = ({ onOpenTermsModal }) => {
  return (
    <Stack
      sx={(theme) => ({
        bgcolor: theme.palette["neutral.700"].main,
        zIndex: 3,
        position: "relative",
      })}
    >
      <InfoSection
        title={filmFestivalGlobals.rulesAndRequirements.contestRules.title}
        subtitle={filmFestivalGlobals.rulesAndRequirements.contestRules.subtitle}
      >
        <RuleList
          rules={filmFestivalGlobals.rulesAndRequirements.contestRules.rules}
          viewTermsText={filmFestivalGlobals.rulesAndRequirements.contestRules.viewTermsText}
          onOpenTermsModal={onOpenTermsModal}
        />
      </InfoSection>
      <InfoSection
        title={filmFestivalGlobals.rulesAndRequirements.technicalRequirements.title}
        subtitle={filmFestivalGlobals.rulesAndRequirements.technicalRequirements.subtitle}
      >
        <RuleList rules={filmFestivalGlobals.rulesAndRequirements.technicalRequirements.rules} />
      </InfoSection>
      <InfoSection
        title={filmFestivalGlobals.rulesAndRequirements.faq.title}
        subtitle={filmFestivalGlobals.rulesAndRequirements.faq.subtitle}
      >
        <QuestionsAndAnswers items={filmFestivalGlobals.rulesAndRequirements.faq.items} />
      </InfoSection>
    </Stack>
  )
}

export default RulesAndRequirements

interface RuleListProps {
  rules: string[]
  viewTermsText?: string
  onOpenTermsModal?: () => void
}

// A sub component for the list of rules
const RuleList: FC<RuleListProps> = ({ rules, viewTermsText, onOpenTermsModal }) => {
  return (
    <Stack spacing={3}>
      {rules.map((rule) => (
        <Typography
          key={rule}
          variant="body.md"
          sx={(theme) => ({
            "color": theme.palette["tertiary.800"].main,
            "fontSize": "16px!important",
            "fontWeight": 600,
            "pl": 4,
            "position": "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: { xs: "14px", md: "15px" },
              transform: "translateY(-50%)",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: (theme) => theme.palette["primary.800"].main,
            },
          })}
        >
          {rule}
        </Typography>
      ))}
      {viewTermsText && (
        <Box paddingLeft={4}>
          <Link
            onClick={onOpenTermsModal}
            sx={(theme) => ({
              color: theme.palette["tertiary.800"].main,
              fontSize: "16px!important",
              fontWeight: 600,
              width: "fit-content",
              paddingBottom: 0,
              cursor: "pointer",
            })}
          >
            {viewTermsText}
          </Link>
        </Box>
      )}
    </Stack>
  )
}

interface QuestionsAndAnswersProps {
  items: {
    question: string
    answer: string
  }[]
}

// A sub component for the FAQ accordion
const QuestionsAndAnswers: FC<QuestionsAndAnswersProps> = ({ items }) => {
  return (
    <Stack gap={4}>
      {items.map((item) => (
        <FAQAccordion key={item.question} item={item} />
      ))}
    </Stack>
  )
}

const StyledAccordion = styled(Accordion)(
  ({ theme }) => `
  margin: 0 auto;
  width: 100%;
  background-color: transparent;
  border-radius: ${theme.spacing(1)};
  border: 1px solid rgba(243, 234, 210, 0.20);
  padding: ${theme.spacing(3, 5, 3, 6)};
  transition: all 0.2s ease-in-out;
  &.Mui-expanded {
    border: 1px solid ${theme.palette["tertiary.800"].main};
    padding: ${theme.spacing(3, 5, 4, 6)};
  }
  
  & .MuiAccordionSummary-root {
    background-color: transparent;
    padding: 0;
    min-height: 0;
    border: none;
  }

  & .MuiAccordionDetails-root {
    padding: ${theme.spacing(2, 5, 0, 0)};
    background-color: transparent;
  }
`
)

interface FAQAccordionProps {
  item: {
    question: string
    answer: string
  }
}

// A sub component for the FAQ accordion
const FAQAccordion: FC<FAQAccordionProps> = ({ item }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      square
    >
      <AccordionSummary
        expandIcon={
          <SvgIcon
            viewBox={expanded ? "0 0 12 2" : "0 0 12 12"}
            size={12}
            variant={expanded ? "minus" : "plus"}
            sx={{
              fill: (theme) => theme.palette["primary.800"].main,
            }}
          />
        }
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body.md"
            sx={(theme) => ({
              color: theme.palette["tertiary.800"].main,
              fontSize: "16px!important",
              fontWeight: 600,
              lineHeight: "160%",
            })}
          >
            {item.question}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography
          variant="body.md"
          sx={(theme) => ({
            color: theme.palette["tertiary.800"].main,
            fontSize: "16px!important",
            fontWeight: 400,
            lineHeight: "150%",
          })}
        >
          {item.answer}
        </Typography>
      </AccordionDetails>
    </StyledAccordion>
  )
}
